import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { Plus, X, AlertTriangle, CheckCircle2, ChevronDown, Lock, Pencil, Trash2, Mic, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { VoiceCaptureDialog } from "./VoiceCaptureDialog";
import { PhotoCaptureDialog } from "./PhotoCaptureDialog";

/** A raw private capture (signal) for the receipt's period, shown read-only. */
export type EditorCapture = { id: string | number; text: string };
import type { ReceiptContent } from "@shared/receipt";
import {
  editorReducer,
  initialState,
  isStaleConflictError,
  shouldPersist,
  type ListField,
  type MovableField,
} from "./receiptEditor.reducer";

const DEBOUNCE_MS = 300;

/**
 * Inline, autosave-on-blur Receipt editor.
 *
 * - Each field renders a `contenteditable` span. Editing mutates local state
 *   immediately (optimistic), and a debounced `onPersist(next)` fires 300ms
 *   after the last keystroke.
 * - Cmd/Ctrl+Enter from anywhere submits Approve.
 * - Enter on a single-line field commits and moves focus forward.
 * - `beforeunload` flushes any in-flight edit before the tab closes.
 * - There is no Save button.
 */
export function ReceiptEditor({
  initial,
  initialUpdatedAt,
  onPersist,
  onApprove,
  approveLabel,
  onRegenerate,
  regenerating,
  captures,
  capturesPeriodLabel = "period",
  onEditCapture,
  onDeleteCapture,
}: {
  initial: ReceiptContent;
  /** The receipt's current `updatedAt` — used as the OCC baseline. */
  initialUpdatedAt?: Date | null;
  /**
   * Persist a snapshot. Receives the editor's last-known `updatedAt`
   * baseline; must return the row's new `updatedAt` (or null) on success.
   * Throw with a `CONFLICT` code/message to mark the editor stale.
   */
  onPersist: (
    next: ReceiptContent,
    expectedUpdatedAt: Date | null,
  ) => Promise<{ updatedAt: Date | null }>;
  onApprove?: () => void;
  /** Label for the on-screen approve CTA (e.g. "Re-approve & share"). */
  approveLabel?: string;
  /** Re-run the AI draft over ALL signals (incl. new captures), REPLACING the
   *  current content. Optional — only shown when provided. */
  onRegenerate?: () => void;
  regenerating?: boolean;
  /** Raw private captures for this receipt's period, shown with one-tap "add as
   *  highlight" + edit/delete so nothing you captured gets lost in the summary. */
  captures?: EditorCapture[];
  /** "day" / "week" — used in the captures panel header. */
  capturesPeriodLabel?: string;
  /** Edit a capture's text (owner-scoped). Enables the per-row pencil. */
  onEditCapture?: (id: string | number, text: string) => void;
  /** Delete a capture (owner-scoped). Enables the per-row trash. */
  onDeleteCapture?: (id: string | number) => void;
}) {
  const { t, locale } = useI18n();
  const isAr = locale === "ar";
  // Default approve CTA, localized at the locale (callers may override).
  const approveCta = approveLabel ?? (isAr ? "اعتماد ومشاركة" : "Approve & share");
  const [state, dispatch] = useReducer(editorReducer, initial, initialState);
  const [showCaptures, setShowCaptures] = useState(true);
  // Explicit confirm for the destructive "Regenerate" (it replaces edits). The
  // bar stays until the user picks Cancel or Regenerate — no auto-dismiss.
  const [regenArmed, setRegenArmed] = useState(false);
  // Inline edit of a capture inside the panel.
  const [capEditId, setCapEditId] = useState<string | number | null>(null);
  const [capEditText, setCapEditText] = useState("");
  // Dictate / Scan into receipt: voice or photo-OCR → classified items added
  // straight to content (no signal created).
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const applyClassified = (c: { highlights: string[]; shipped: string[]; blockers: string[] }) => {
    for (const h of c.highlights) dispatch({ type: "addListItem", field: "highlights", value: h });
    for (const s of c.shipped) dispatch({ type: "addListItem", field: "shipped", value: s });
    for (const b of c.blockers) dispatch({ type: "addBlocker", text: b });
  };
  const [stale, setStale] = useState(false);

  // Re-sync if a fresh server snapshot replaces the prop.
  //
  // CRITICAL guard: skip the reset when the user has unsaved local
  // edits (`state.dirtyAt !== 0`). The autosave loop triggers a
  // React Query refetch on every persisted snapshot, and the parent
  // hands us a new `initial` reference even when the content is
  // identical. Without this guard, an in-flight save races with a
  // mid-word keystroke — the reset clobbers the typing back to the
  // server snapshot, the InlineText sees a value mismatch with its
  // DOM, and the syncing useEffect rewrites textContent (caret to
  // position 0). The reset is still essential for genuine external
  // changes (Approve → status update, regenerate, server-driven
  // edits), all of which clear dirtyAt before the new content
  // arrives.
  const initialRef = useRef(initial);
  const stateRefForReset = useRef(state);
  stateRefForReset.current = state;
  useEffect(() => {
    if (initial !== initialRef.current) {
      initialRef.current = initial;
      if (stateRefForReset.current.dirtyAt === 0) {
        dispatch({ type: "reset", content: initial });
        setStale(false);
      }
    }
  }, [initial]);

  // OCC baseline — track the most recent updatedAt the server has accepted.
  const baselineRef = useRef<Date | null>(initialUpdatedAt ?? null);
  useEffect(() => {
    baselineRef.current = initialUpdatedAt ?? null;
  }, [initialUpdatedAt]);

  // Debounced persist driven by the pure shouldPersist helper.
  const stateRef = useRef(state);
  stateRef.current = state;
  const persistRef = useRef(onPersist);
  persistRef.current = onPersist;
  const staleRef = useRef(false);
  staleRef.current = stale;

  const flushNow = useCallback(async () => {
    if (staleRef.current) return;
    const cur = stateRef.current;
    if (cur.dirtyAt === 0) return;
    const snapshot = cur.content;
    dispatch({ type: "markPersisted" });
    try {
      const result = await persistRef.current(snapshot, baselineRef.current);
      if (result?.updatedAt) baselineRef.current = result.updatedAt;
    } catch (err: any) {
      if (isStaleConflictError(err)) {
        // A conflict here is almost always BENIGN. This product has a
        // single author per receipt, and the dominant trigger is the
        // user's own Approve: `receipts.updatedAt` is `.onUpdateNow()`,
        // so approving bumps the OCC token server-side a beat before the
        // post-approve refetch propagates the new baseline back into this
        // editor. Treating that as a hard conflict silently DROPS the
        // edit the user just made (e.g. a just-added item) AND leaves the
        // receipt stuck at "approved" instead of flipping to "edited"
        // (needs re-approval) — the exact "I approved then added an item
        // and it didn't convert" regression.
        //
        // So rebase once and retry without the precondition
        // (last-write-wins — the `receipts.edit` input explicitly
        // documents omitting it as acceptable). Only fall back to the
        // stale banner if the retry ALSO fails: a genuine, persistent
        // conflict (e.g. the row vanished), where reload is the right
        // recovery.
        try {
          const retry = await persistRef.current(snapshot, null);
          if (retry?.updatedAt) baselineRef.current = retry.updatedAt;
        } catch {
          setStale(true);
        }
        return;
      }
      // Other errors: bubble to the surrounding mutation surface but keep
      // optimistic state so typing isn't lost.
    }
  }, []);

  useEffect(() => {
    if (state.dirtyAt === 0) return;
    const decision = shouldPersist(state, Date.now(), DEBOUNCE_MS);
    if (decision.action === "save") {
      flushNow();
      return;
    }
    const handle = window.setTimeout(() => {
      const recheck = shouldPersist(stateRef.current, Date.now(), DEBOUNCE_MS);
      if (recheck.action === "save") flushNow();
    }, decision.waitMs);
    return () => window.clearTimeout(handle);
  }, [state, flushNow]);

  // Flush on tab close.
  useEffect(() => {
    function onBeforeUnload() {
      if (stateRef.current.dirtyAt !== 0 && !staleRef.current) {
        // Best-effort synchronous flush; we intentionally don't await.
        void persistRef.current(stateRef.current.content, baselineRef.current);
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Flush on UNMOUNT (dialog close). Without this, closing the editor within
  // the 300ms debounce window silently dropped the last edit — e.g. delete the
  // headline and immediately tap Done/X, and the old headline survived in the
  // DB while the lists (persisted by earlier ticks) were gone.
  useEffect(() => {
    return () => {
      if (stateRef.current.dirtyAt !== 0 && !staleRef.current) {
        void persistRef.current(stateRef.current.content, baselineRef.current);
      }
    };
  }, []);

  // Cmd/Ctrl+Enter from anywhere in the editor approves.
  function onKeyDownContainer(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void flushNow().then(() => onApprove?.());
    }
  }

  return (
    <div
      className="mt-5 space-y-5 border-t border-border pt-5"
      onKeyDown={onKeyDownContainer}
    >
      {stale && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 flex items-start gap-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-amber-900 dark:text-amber-200">
              {t.editorStaleTitle}
            </div>
            <div className="text-xs text-amber-800 dark:text-amber-200/80 mt-0.5">
              {t.editorStaleBody}
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm underline hover:no-underline shrink-0"
          >
            {t.retry}
          </button>
        </div>
      )}

      {captures && captures.length > 0 ? (
        <div className="rounded-xl border border-border bg-muted/30 p-3" data-testid="editor-captures">
          <button
            type="button"
            onClick={() => setShowCaptures(s => !s)}
            className="flex w-full items-center justify-between text-xs font-semibold text-muted-foreground"
          >
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" />{" "}
              {isAr
                ? `ما التُقط هذا ${capturesPeriodLabel} · ${captures.length}`
                : `Captured this ${capturesPeriodLabel} · ${captures.length}`}
            </span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", showCaptures && "rotate-180")} />
          </button>
          {showCaptures ? (
            <>
              <ul className="mt-2 space-y-1.5">
                {captures.map(c => {
                  const editing = capEditId === c.id;
                  return (
                    <li key={c.id} className="flex items-center gap-1.5 text-sm">
                      {editing ? (
                        <>
                          <input
                            value={capEditText}
                            onChange={e => setCapEditText(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                const v = capEditText.trim();
                                if (v) onEditCapture?.(c.id, v.slice(0, 280));
                                setCapEditId(null);
                              }
                              if (e.key === "Escape") setCapEditId(null);
                            }}
                            autoFocus
                            className="min-w-0 flex-1 rounded-md border border-coral bg-background px-2 py-1 text-sm outline-none"
                          />
                          <button
                            type="button"
                            aria-label={t.save}
                            onClick={() => {
                              const v = capEditText.trim();
                              if (v) onEditCapture?.(c.id, v.slice(0, 280));
                              setCapEditId(null);
                            }}
                            className="shrink-0 rounded-md p-1 text-emerald-600 hover:bg-muted"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                          <button type="button" aria-label={t.cancel} onClick={() => setCapEditId(null)} className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted">
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="min-w-0 flex-1 break-words text-foreground/90">{c.text}</span>
                          <button
                            type="button"
                            onClick={() => dispatch({ type: "addListItem", field: "highlights", value: c.text })}
                            className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-coral-soft/40 hover:text-coral"
                          >
                            <Plus className="h-3 w-3" /> {isAr ? "إضافة" : "add"}
                          </button>
                          {onEditCapture ? (
                            <button
                              type="button"
                              aria-label={isAr ? "تعديل التحديث" : "Edit capture"}
                              onClick={() => {
                                setCapEditId(c.id);
                                setCapEditText(c.text);
                              }}
                              className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                          {onDeleteCapture ? (
                            <button
                              type="button"
                              aria-label={isAr ? "حذف التحديث" : "Delete capture"}
                              onClick={() => onDeleteCapture(c.id)}
                              className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2 text-[11px] text-muted-foreground">
                {isAr
                  ? "تحديثاتك الخام خاصة بك. اضغط «إضافة» لإدراج أحدها ضمن أبرز الإنجازات، أو عدّل/احذف."
                  : "Your raw captures are private to you. Tap “add” to include one as a highlight, or edit/delete."}
              </p>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Dictate / Scan — voice & photo feed straight into this receipt's
          highlights / shipped / blockers (no signal created). */}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setVoiceOpen(true)}
          className="rounded-full border-coral/40 text-coral hover:bg-coral-soft/40"
          data-testid="editor-dictate"
        >
          <span className="inline-flex items-center gap-1.5">
            <Mic className="h-4 w-4" /> {isAr ? "إملاء" : "Dictate"}
          </span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setPhotoOpen(true)}
          className="rounded-full border-coral/40 text-coral hover:bg-coral-soft/40"
          data-testid="editor-scan"
        >
          <span className="inline-flex items-center gap-1.5">
            <Camera className="h-4 w-4" /> {isAr ? "مسح ضوئي" : "Scan"}
          </span>
        </Button>
      </div>
      <VoiceCaptureDialog
        open={voiceOpen}
        onOpenChange={setVoiceOpen}
        onLogged={() => {}}
        onApply={applyClassified}
      />
      <PhotoCaptureDialog
        open={photoOpen}
        onOpenChange={setPhotoOpen}
        onLogged={() => {}}
        onApply={applyClassified}
      />

      <div>
        <FieldLabel>{t.headline}</FieldLabel>
        <InlineText
          value={state.content.headline}
          singleLine
          className="mt-1.5 font-display text-lg sm:text-xl"
          onChange={value => dispatch({ type: "setHeadline", value })}
        />
      </div>

      <ListField
        title={t.highlights}
        field="highlights"
        state={state}
        dispatch={dispatch}
        moveTargets={[
          { to: "shipped", label: `→ ${t.shipped}` },
          { to: "blockers", label: `→ ${t.blockers}` },
        ]}
      />
      <ListField
        title={t.shipped}
        field="shipped"
        state={state}
        dispatch={dispatch}
        moveTargets={[
          { to: "highlights", label: `→ ${t.highlights}` },
          { to: "blockers", label: `→ ${t.blockers}` },
        ]}
      />

      <div>
        <FieldLabel>{t.blockers}</FieldLabel>
        <div className="space-y-2 mt-1.5">
          {state.content.blockers.map((b, i) => (
            <div key={i} className="flex gap-2 items-start">
              <InlineText
                value={b.text}
                className="flex-1"
                placeholder="—"
                onChange={value =>
                  dispatch({ type: "setBlockerText", index: i, value })
                }
              />
              <InlineText
                value={b.owner ?? ""}
                singleLine
                className="max-w-[160px] text-muted-foreground"
                placeholder={isAr ? "المسؤول" : "owner"}
                onChange={value =>
                  dispatch({ type: "setBlockerOwner", index: i, value })
                }
              />
              {(
                [
                  { to: "highlights", label: `→ ${t.highlights}` },
                  { to: "shipped", label: `→ ${t.shipped}` },
                ] as Array<{ to: MovableField; label: string }>
              ).map(mt => (
                <button
                  key={mt.to}
                  type="button"
                  onClick={() =>
                    dispatch({
                      type: "moveItem",
                      from: "blockers",
                      index: i,
                      to: mt.to,
                    })
                  }
                  className="shrink-0 text-[11px] rounded-full border border-border px-2 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  title={mt.label}
                >
                  {mt.label}
                </button>
              ))}
              <RemoveButton
                onClick={() => dispatch({ type: "removeBlocker", index: i })}
              />
            </div>
          ))}
          <AddButton onClick={() => dispatch({ type: "addBlocker" })} />
        </div>
      </div>

      {/* Thanks section removed from daily + weekly reports per
          product decision. The `thanks` field remains in the schema
          (no migration / data loss) but is no longer surfaced or
          editable in the receipt UI. */}

      {/* On-screen Approve CTA. Flushes any pending autosave FIRST so the
          approval preview + snapshot use the latest edits (not stale content),
          then hands off to the parent which opens the "This is what your team
          will see" preview. Mirrors the Cmd/Ctrl+Enter shortcut. */}
      {onApprove || onRegenerate ? (
        <div className="space-y-2 pt-1">
          {/* Explicit confirm for the destructive regenerate — a clear Cancel /
              Regenerate pair, not a re-asking button. */}
          {onRegenerate && regenArmed ? (
            <div className="flex flex-col gap-2 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs sm:flex-row sm:items-center sm:justify-between">
              <span className="text-amber-900 dark:text-amber-200">
                {isAr
                  ? "إعادة الإنشاء بالذكاء الاصطناعي؟ سيستبدل هذا الملخّص الحالي بمسودة جديدة من تحديثاتك."
                  : "Regenerate with AI? This replaces the current receipt with a fresh draft from your captures."}
              </span>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRegenArmed(false)}>
                  {t.cancel}
                </Button>
                <Button
                  size="sm"
                  className="rounded-full"
                  disabled={!!regenerating}
                  data-testid="editor-regenerate-confirm"
                  onClick={() => {
                    setRegenArmed(false);
                    onRegenerate();
                  }}
                >
                  {regenerating ? "…" : isAr ? "إعادة الإنشاء" : "Regenerate"}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2">
            {onRegenerate && !regenArmed ? (
              <Button
                variant="outline"
                disabled={!!regenerating}
                onClick={() => setRegenArmed(true)}
                className="rounded-full max-sm:w-full"
                data-testid="editor-regenerate"
                title={
                  isAr
                    ? "إعادة تشغيل الذكاء الاصطناعي على كل التحديثات (بما فيها الجديدة). يستبدل المحتوى الحالي."
                    : "Re-run the AI over all signals (including new captures). Replaces current content."
                }
              >
                {regenerating
                  ? isAr
                    ? "جارٍ إعادة الإنشاء…"
                    : "Regenerating…"
                  : isAr
                    ? "إعادة الإنشاء بالذكاء الاصطناعي"
                    : "Regenerate with AI"}
              </Button>
            ) : null}
            {onApprove ? (
              <Button
                onClick={() => void flushNow().then(() => onApprove())}
                className="rounded-full max-sm:w-full"
                data-testid="editor-approve"
              >
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> {approveCta}
                </span>
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ListField({
  title,
  field,
  state,
  dispatch,
  moveTargets,
}: {
  title: string;
  field: ListField;
  state: ReturnType<typeof initialState>;
  dispatch: React.Dispatch<any>;
  /** Per-row "move this line elsewhere" actions (e.g. a highlight →
   *  shipped or → blockers) so the user re-files without retyping.
   *  Labels are passed pre-localized by the parent. */
  moveTargets?: Array<{ to: MovableField; label: string }>;
}) {
  const items = state.content[field];
  return (
    <div>
      <FieldLabel>{title}</FieldLabel>
      <div className="space-y-2 mt-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-start">
            <InlineText
              value={it}
              className="flex-1"
              placeholder="—"
              onChange={value =>
                dispatch({ type: "setListItem", field, index: i, value })
              }
            />
            {(moveTargets ?? []).map(mt => (
              <button
                key={mt.to}
                type="button"
                onClick={() =>
                  dispatch({
                    type: "moveItem",
                    from: field,
                    index: i,
                    to: mt.to,
                  })
                }
                className="shrink-0 text-[11px] rounded-full border border-border px-2 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                title={mt.label}
              >
                {mt.label}
              </button>
            ))}
            <RemoveButton
              onClick={() => dispatch({ type: "removeListItem", field, index: i })}
            />
          </div>
        ))}
        <AddButton onClick={() => dispatch({ type: "addListItem", field })} />
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
      {children}
    </label>
  );
}

function AddButton({ onClick }: { onClick: () => void }) {
  const { locale } = useI18n();
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs text-muted-foreground hover:text-coral inline-flex items-center gap-1 px-1.5 py-1 rounded-md hover:bg-coral-soft/40"
    >
      <Plus className="h-3.5 w-3.5" /> {locale === "ar" ? "إضافة" : "add"}
    </button>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  const { locale } = useI18n();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={locale === "ar" ? "إزالة" : "Remove"}
      className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-muted"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

/**
 * A controlled `contenteditable` span. We avoid React's value-prop reconciliation
 * fighting with browser caret state by writing into the DOM only when the
 * external value diverges from the live textContent.
 */
function InlineText({
  value,
  onChange,
  singleLine,
  className,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  singleLine?: boolean;
  className?: string;
  placeholder?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  // Track the last text we emitted ourselves via onInput. When the
  // incoming `value` prop equals this, we know the value just made
  // the React round-trip from our own keystroke and we should NOT
  // touch the DOM — touching `textContent` collapses the caret to
  // position 0, which is the "cursor jumps on space" bug.
  //
  // Crucially: initialise to a sentinel `null`, NOT to `value`. If we
  // pre-seed with `value`, the very first effect run on mount sees
  // `value === lastEmittedRef.current` and skips setting the DOM
  // textContent — leaving the field visually empty after a page
  // refresh even though the data was loaded correctly.
  const lastEmittedRef = useRef<string | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Skip only when we've recorded a previous emission AND the new
    // value matches it. On first mount `lastEmittedRef.current` is
    // null → the guard falls through → we set the initial DOM
    // textContent below.
    if (lastEmittedRef.current !== null && value === lastEmittedRef.current) {
      return;
    }
    if (node.textContent !== value) {
      node.textContent = value;
    }
    lastEmittedRef.current = value;
  }, [value]);

  return (
    <span
      ref={ref}
      role="textbox"
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder ?? ""}
      onInput={e => {
        // We do NOT normalise here. The browser may insert NBSP at
        // word boundaries; that's fine. Stamp the emitted text into
        // `lastEmittedRef` so the syncing useEffect skips its
        // DOM-rewrite when the value bounces back unchanged from
        // React state. Without that bookkeeping, every space the
        // user types reset the caret to position 0.
        const text = e.currentTarget.textContent ?? "";
        lastEmittedRef.current = text;
        onChange(text);
      }}
      onBlur={e => {
        const trimmed = (e.currentTarget.textContent ?? "").trimEnd();
        lastEmittedRef.current = trimmed;
        onChange(trimmed);
      }}
      onKeyDown={e => {
        if (singleLine && e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          (e.currentTarget as HTMLElement).blur();
          // Move focus to the next focusable contenteditable in tab order.
          const all = Array.from(
            document.querySelectorAll<HTMLElement>('[contenteditable="true"]'),
          );
          const idx = all.indexOf(e.currentTarget as HTMLElement);
          all[idx + 1]?.focus();
        }
      }}
      className={
        "inline-block min-w-[3ch] outline-none rounded-md px-1 -mx-1 leading-snug " +
        "focus:bg-coral-soft/40 hover:bg-muted/40 transition-colors empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/50 " +
        (className ?? "")
      }
    />
  );
}
